/**
 * Authentication Store
 * 
 * Manages authentication state using Zustand.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, profileApi } from '../lib/api';
import { usePreferencesStore } from './preferences';

interface User {
  id: string;
  email: string;
  role: string;
  avatarUrl?: string;
}

export type AdminLoginPending = { expiresAt: string; emailMasked: string };

export type LoginFlowResult =
  | { needsAdminCode: false }
  | { needsAdminCode: true; expiresAt: string; emailMasked: string };

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginFlowResult>;
  verifyAdminLoginCode: (email: string, code: string) => Promise<void>;
  resendAdminLoginCode: (email: string, password: string) => Promise<AdminLoginPending>;
  register: (email: string, password: string, role?: 'admin' | 'user') => Promise<void>;
  activateAccount: (email: string, activationToken: string) => Promise<void>;
  resendActivationEmail: (email: string) => Promise<void>;
  logout: () => void;
  setToken: (token: string) => void;
  completeSessionWithToken: (accessToken: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => {
      const completeSessionWithToken = async (accessToken: string) => {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const user: User = {
          id: payload.sub,
          email: payload.email,
          role: payload.role,
        };

        set({
          token: accessToken,
          user,
          isAuthenticated: true,
        });

        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        try {
          const profile = await profileApi.getProfile();
          const preferencesStore = usePreferencesStore.getState();

          if (profile.theme) {
            preferencesStore.setTheme(profile.theme as 'dark' | 'light' | 'system');
          }
          if (profile.compactMode !== undefined) {
            preferencesStore.setCompactMode(profile.compactMode);
          }
          if (profile.timezone) {
            preferencesStore.setTimezone(profile.timezone as any);
          }
          if (profile.dateFormat) {
            preferencesStore.setDateFormat(profile.dateFormat as any);
          }
        } catch (prefError) {
          console.warn('Failed to load user preferences:', prefError);
        }
      };

      return {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string): Promise<LoginFlowResult> => {
        set({ isLoading: true });
        try {
          const response = await api.post('/api/v1/auth/login', {
            email,
            password,
          });

          const data = response.data;

          if (data.requiresEmailCode === true) {
            set({ isLoading: false });
            return {
              needsAdminCode: true,
              expiresAt: data.expiresAt,
              emailMasked: data.emailMasked,
            };
          }

          await completeSessionWithToken(data.accessToken);
          set({ isLoading: false });
          return { needsAdminCode: false };
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      verifyAdminLoginCode: async (email: string, code: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/api/v1/auth/login/verify-admin-code', {
            email,
            code: code.trim(),
          });
          await completeSessionWithToken(response.data.accessToken);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resendAdminLoginCode: async (email: string, password: string): Promise<AdminLoginPending> => {
        const response = await api.post('/api/v1/auth/login/resend-admin-code', {
          email,
          password,
        });
        return {
          expiresAt: response.data.expiresAt,
          emailMasked: response.data.emailMasked,
        };
      },

      register: async (email: string, password: string, role: 'admin' | 'user' = 'user') => {
        set({ isLoading: true });
        try {
          // Register the user
          await api.post('/api/v1/auth/register', {
            email,
            password,
            role,
          });

          // Don't auto-login - user needs to activate account first
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      activateAccount: async (email: string, activationToken: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/v1/auth/activate', {
            email,
            activationToken,
          });
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resendActivationEmail: async (email: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/v1/auth/resend-activation', {
            email,
          });
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        delete api.defaults.headers.common['Authorization'];
      },

      setToken: (token: string) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        set({ token });
      },

      completeSessionWithToken,
    };
    },
    {
      name: 'gpu-platform-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Restore token to API client on rehydration
        if (state?.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
          
          // Load user preferences if authenticated
          if (state?.isAuthenticated && state?.user) {
            profileApi.getProfile()
              .then((profile) => {
                const preferencesStore = usePreferencesStore.getState();
                
                // Apply theme preference
                if (profile.theme) {
                  preferencesStore.setTheme(profile.theme as 'dark' | 'light' | 'system');
                }
                
                // Apply compact mode preference
                if (profile.compactMode !== undefined) {
                  preferencesStore.setCompactMode(profile.compactMode);
                }
                
                // Apply timezone preference
                if (profile.timezone) {
                  preferencesStore.setTimezone(profile.timezone as any);
                }
                
                // Apply date format preference
                if (profile.dateFormat) {
                  preferencesStore.setDateFormat(profile.dateFormat as any);
                }
              })
              .catch((error) => {
                // If preference loading fails, continue with defaults
                console.warn('Failed to load user preferences on rehydration:', error);
              });
          }
        }
      },
    },
  ),
);
